import { ArrowPathIcon, IdentificationIcon, ScaleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../store/store';
import type { CardHolderDetails, CardHolderListItem } from '../../../../utils/cardHolders';
import { formatIsoDay } from '../../../../utils/cardHolders';
import { JWTHeader } from '../../../../utils/JWTHeader';
import KPICard from '../../../common/KPICards';
import Popup from '../../../common/Popup';
import {
  CompanyAddressContactBlock,
  CompanyLegalInfoBlock,
  CompanyLotsCards,
  CompanyServicesBlock,
} from '../../components/CompanyInfoBlocks';
import { FederationLogos } from '../../components/CompanyListFilters';

interface CardHolderModalProps {
  holder: CardHolderListItem | null;
  dataDate: string | null;
  onClose: () => void;
}

/**
 * What we know about a card holder who may not be in our base: the informative blocks of the company page,
 * without anything that needs a company of ours (subscription, copros, sales, employees, links).
 */
export default function CardHolderModal({ holder, dataDate, onClose }: CardHolderModalProps) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [details, setDetails] = useState<CardHolderDetails | null>(null);
  // Remounted per holder (key): starts loading so no stale or empty details show before the fetch
  const [loading, setLoading] = useState(holder !== null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const holderId = holder?.id;

  const fetchDetails = useCallback(async(id: number, signal: AbortSignal) => {
    setLoading(true);
    setError(false);
    setDetails(null);
    try {
      const response = await fetch(`/api/admin/card-holders/${id}`, { ...JWTHeader(jwt), signal });
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      setDetails(await response.json());
    } catch (fetchError) {
      if (signal.aborted) {return;}
      console.error('Error fetching card holder details :', fetchError);
      setError(true);
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [jwt]);

  useEffect(() => {
    if (holderId === undefined) {return undefined;}
    const controller = new AbortController();
    fetchDetails(holderId, controller.signal);
    return () => controller.abort();
  }, [holderId, attempt, fetchDetails]);

  useEffect(() => {
    if (holderId !== undefined) {
      contentRef.current?.focus();
    }
  }, [holderId]);

  if (!holder) {return null;}

  const lots = { nbLotsHab: holder.nbLotsHab, nbLotsHabBurCom: holder.nbLotsTotal };

  return (
    <Popup
      isOpen
      onClose={onClose}
      title={holder.name}
      width="72rem"
    >
      <div ref={contentRef} tabIndex={-1} className="outline-none" aria-label={`Informations sur ${holder.name}`}>
        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="max-w-prose text-sm text-gray-600">
            Entreprise de la base des cartes professionnelles, pas forcément présente dans Croissance.
          </p>
          <div className="flex shrink-0 items-center gap-4">
            <FederationLogos details={details ?? holder.details} variant="header" />
          </div>
        </div>

        {error ? (
          <div className="mb-4 flex flex-col gap-3 rounded-lg bg-red-100 p-4 text-red-800 sm:flex-row sm:items-center sm:justify-between">
            <p>Les informations de cette entreprise n'ont pas pu être chargées.</p>
            <button
              type="button"
              onClick={() => setAttempt((value) => value + 1)}
              className="flex shrink-0 items-center justify-center rounded bg-red-700 px-4 py-2 text-sm text-white transition-colors hover:bg-red-800"
            >
              <ArrowPathIcon className="mr-2 h-4 w-4" />
              Réessayer
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <KPICard
                icon={<IdentificationIcon className="h-6 w-6" />}
                title="SIREN"
                value={holder.siren ?? 'N/A'}
                copy={Boolean(holder.siren)}
              />
              <KPICard
                icon={<ScaleIcon className="h-6 w-6" />}
                title="Forme juridique"
                value={details?.formeJuridique ?? 'N/A'}
                loading={loading}
              />
            </div>
            <CompanyAddressContactBlock details={details} loading={loading} />
            <CompanyLegalInfoBlock details={details} loading={loading} />
            <CompanyServicesBlock details={details} loading={loading} />
          </>
        )}
        <CompanyLotsCards details={lots} loading={false} />

        <p className="mt-4 text-xs text-gray-600">
          Sources : fichier CCI des cartes professionnelles{dataDate ? ` (données du ${formatIsoDay(dataDate)})` : ''},
          DJ, annuaires FNAIM et SNPI, registre national d'immatriculation des copropriétés.
        </p>
      </div>
    </Popup>
  );
}
